import { Box, IconButton, Tooltip } from '@mui/material'
import { useState } from 'react';
import CustomDialog from 'components/dialogs/CustomDialog';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import DeleteIcon from 'icons/DeleteIcon';
import { colors } from 'styles/colors';
import {
  useDeleteProductSuggestion,
  usePermanentlyDeleteProductSuggestion,
  useRestoreProductSuggestion,
} from 'hooks/useProductSuggestion';
import { snackbarService } from 'services/snackbarService';
import { getErrorMessage } from 'common/utils';

interface Props {
  id: string;
  refetch?: () => void;
  isProductAdded?: boolean;
  // Deleted-suggestions view: show Restore / Delete Permanently instead of Create/Delete.
  deletedView?: boolean;
}

export default function ProductSuggestionActionButtons({ id, refetch, isProductAdded, deletedView }: Props) {
  const [open, setOpen] = useState(false);

  const { mutate: deleteMutation, isLoading: isDeleting } = useDeleteProductSuggestion(
    () => {
      refetch?.();
      snackbarService.showSnackbar('Suggestion deleted successfully', 'success');
      setOpen(false);
    },
    (error: any) => {
      snackbarService.showSnackbar(getErrorMessage(error), 'error');
      setOpen(false);
    }
  );

  const { mutate: restoreMutation, isLoading: isRestoring } = useRestoreProductSuggestion(
    () => {
      refetch?.();
      snackbarService.showSnackbar('Suggestion restored successfully', 'success');
    },
    (error: any) => {
      snackbarService.showSnackbar(getErrorMessage(error), 'error');
    }
  );

  const { mutate: permanentDeleteMutation, isLoading: isPermanentlyDeleting } = usePermanentlyDeleteProductSuggestion(
    () => {
      refetch?.();
      snackbarService.showSnackbar('Suggestion permanently deleted', 'success');
      setOpen(false);
    },
    (error: any) => {
      snackbarService.showSnackbar(getErrorMessage(error), 'error');
      setOpen(false);
    }
  );

  if (deletedView) {
    return (
      <>
        <Box display={'flex'} gap={1} justifyContent={'center'}>
          <CustomButton
            text='Restore'
            variant='outlined'
            height={30}
            loading={isRestoring}
            onClick={() => restoreMutation(id)}
          />
          <Tooltip title={'Delete Permanently'}>
            <IconButton
              onClick={() => setOpen(true)}
              sx={{
                width: 25,
                height: 25,
                backgroundColor: colors.error,
                borderRadius: '4px',
                padding: 0,
                '&:hover': {
                  backgroundColor: colors.delete,
                },
              }}
            >
              <DeleteIcon color={colors.white} size={13} />
            </IconButton>
          </Tooltip>
        </Box>

        <CustomDialog
          open={open}
          title={'Delete Permanently'}
          message={'This cannot be undone. Are you sure you want to permanently delete this suggestion?'}
          buttons={[
            <CustomButton
              key='cancel'
              variant='outlined'
              text='Cancel'
              onClick={() => setOpen(false)}
              disabled={isPermanentlyDeleting}
              height={35}
            />,
            <CustomButton
              key='delete'
              variant='filled'
              color={'error'}
              text={'Delete Permanently'}
              onClick={() => permanentDeleteMutation(id)}
              loading={isPermanentlyDeleting}
              height={35}
            />,
          ]}
        />
      </>
    );
  }

  return (
    <>
      <Box display={'flex'} gap={1}>
        <Tooltip title={'Delete'}>
          <IconButton
            onClick={() => setOpen(true)}
            sx={{
              width: 25,
              height: 25,
              backgroundColor: colors.error,
              borderRadius: '4px',
              padding: 0,
              '&:hover': {
                backgroundColor: colors.delete,
              },
            }}
          >
            <DeleteIcon color={colors.white} size={13}/>
          </IconButton>
        </Tooltip>
      </Box>

      <CustomDialog
        open={open}
        title={'Delete Suggestion'}
        message={'Are you sure you want to delete this suggestion? You can restore it later from Deleted Suggestions.'}
        buttons={[
          <CustomButton
            key='cancel'
            variant='outlined'
            text='Cancel'
            onClick={() => setOpen(false)}
            disabled={isDeleting}
            height={35}
          />,
          <CustomButton
            key='delete'
            variant='filled'
            color={'error'}
            text={'Delete'}
            onClick={() => deleteMutation(id)}
            loading={isDeleting}
            height={35}
          />,
        ]}
      />
    </>
  );
}
